/* Loads Plyr the first time someone actually wants a video — and not before.

   Plyr costs 110KB of script and 31KB of CSS. Both used to be requested on
   every page load, by two sections each, for a control bar that only matters
   once a visitor presses play. The majority of sessions never do, so that was
   141KB of parse and network spent on behalf of a minority, competing with the
   hero image for bandwidth at exactly the wrong moment.

   The tradeoff this makes: the FIRST play is slower, because the library has to
   arrive before the bar can be built. Three things keep that from being felt:

     1. The <video> elements keep their native `controls` attribute in the
        markup. Before Plyr arrives the browser's own controls work — play,
        scrub, fullscreen — so the video is never dead, it just looks like the
        browser's player for a moment. reformly-bp-player.js strips `controls`
        as it upgrades each element.

     2. Loading starts on POINTERDOWN/TOUCHSTART, not on click. That is one
        full interaction ahead of the play itself, and on a touch screen the
        gap between finger-down and finger-up is typically 80-150ms of free
        head start.

     3. A hover over any video surface also triggers it, so on desktop the
        library is usually already there before the first click happens.

   Everything is fetched once and cached on the promise, so N videos on the page
   share one load. */
(function () {
  'use strict';

  /* Kept in step with SURFACES in reformly-bp-player.js — these are the
     elements that get a Plyr bar. .rbp-vcard is deliberately excluded there
     (the 56-clip hero rail keeps native controls), so it is excluded here too
     and never triggers the load. */
  var TRIGGER = '.rbp-gal__slide video, .rbp-card__media video, [data-bp-play]';

  var started = false;

  function asset(name) {
    /* The URLs are stamped onto the player script tag by the section, because
       Liquid is the only thing that knows the CDN path and its version hash.

       The marker attribute is `data-rbp-plyr-assets`, deliberately NOT
       `data-rbp-plyr-js` — that name carries the script URL, and an element
       cannot have the same attribute twice. */
    var tag = document.querySelector('[data-rbp-plyr-assets]');
    return tag ? tag.getAttribute('data-rbp-plyr-' + name) : null;
  }

  function load() {
    if (started) return;
    started = true;

    var css = asset('css');
    if (css) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = css;

      /* INSERTED BEFORE our own stylesheets, never appended.

         The Plyr skin lives in reformly-bp-hero.css as plain overrides of
         plyr.css selectors — same specificity in most cases — so it only wins
         on source order. When this sheet was emitted by the section it sat
         above those overrides in the document and everything resolved. Loading
         it at runtime and appending it to <head> put it LAST, so Plyr's stock
         purple came back and the bar lost its colour scheme.

         Anchoring on the first bp stylesheet keeps the original relative order:
         plyr.css first, our overrides after it. */
      var anchor = document.querySelector('link[href*="reformly-bp-base.css"], link[href*="reformly-bp-hero.css"], link[href*="reformly-base.css"]');
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(link, anchor);
      else document.head.appendChild(link);
    }

    var js = asset('js');
    if (!js) return;

    var s = document.createElement('script');
    s.src = js;
    s.onload = function () {
      /* reformly-bp-player.js is already on the page and has run; it exposes
         this so the upgrade can happen now that window.Plyr exists. */
      if (window.rbpPlayerInit) window.rbpPlayerInit();
    };
    document.head.appendChild(s);
  }

  function maybe(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest(TRIGGER)) load();
  }

  /* Capture phase: a <video> with native controls swallows pointer events on
     its own control layer, so a bubbling listener never sees the press that
     matters most — the one on the play button. */
  ['pointerdown', 'touchstart'].forEach(function (evt) {
    document.addEventListener(evt, maybe, { passive: true, capture: true });
  });
  /* Desktop head start: by the time the click lands the library is usually in. */
  document.addEventListener('pointerover', maybe, { passive: true, capture: true });

  /* Last resort. If a visitor gets to a video by keyboard, or something calls
     play() directly, upgrade rather than leave them on native controls. */
  document.addEventListener('play', maybe, { capture: true });
})();
